// src/hooks/order/useOrderSubmit.tsx
// Hook para manejar el submit del formulario de pedidos

import React, { useCallback } from 'react'
import toast from 'react-hot-toast'
import { transformOrderData } from '../../utils/orderTransform'
import { getDefaultOrderStatusByPaymentMethod } from '../../utils/orderDefaultStatusByPayment'
import { bodegaFacturacion } from '../../assets/data/arrays'
import type { OrderData, PagoParcial } from '../../types/orderFormTypes'
import type { BodegaRecogidaSeleccion, RecogeRespuesta } from './useRecogeEnBodega'
import {
  recogeFlagFromRespuesta,
  resolveBodegaRecogidaForSubmit
} from '../../utils/orderBodegaRecogida'

const BODEGA_FACTURACION_VALUES = new Set(
  bodegaFacturacion.map((b: { value: string }) => b.value)
)

interface UseOrderSubmitProps {
  formData: OrderData
  productDetails: { [key: string]: any }
  costoEnvio: string
  pagosParciales: PagoParcial[]
  recogeRespuesta: RecogeRespuesta
  bodegaRecogidaSeleccion: BodegaRecogidaSeleccion
  fechaEntregaActiva: boolean
  fechaEntrega: string
  mode: 'view' | 'edit' | 'create'
  oldDataOrder: {
    status?: string
    customer_note?: string
    lineItems?: any[]
    transaction_id?: string
  }
  validateForm: () => boolean
  getValidationSummary: () => string[]
  calculateTotals: (lineItems: any[]) => { subtotal: string; discount: string; taxes: string; shipping: string; total: string }
  setIsSubmitting: (isSubmitting: boolean) => void
  onSubmit?: (data: OrderData) => void | Promise<void>
  userRoleId?: number | null
}

interface UseOrderSubmitReturn {
  handleSubmit: (e: React.FormEvent) => void
}

export function useOrderSubmit({
  formData,
  productDetails,
  costoEnvio,
  pagosParciales,
  recogeRespuesta,
  bodegaRecogidaSeleccion,
  fechaEntregaActiva,
  fechaEntrega,
  mode,
  oldDataOrder,
  validateForm,
  getValidationSummary,
  calculateTotals,
  setIsSubmitting,
  onSubmit,
  userRoleId
}: UseOrderSubmitProps): UseOrderSubmitReturn {
  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault()
    
    // Validar formulario
    if (!validateForm()) {
      const missingFields = getValidationSummary()
      
      if (missingFields.length > 0) {
        const message = missingFields.length === 1 
          ? `Falta completar el campo: ${missingFields[0]}`
          : `Faltan completar ${missingFields.length} campos obligatorios:\n• ${missingFields.join('\n• ')}`
        
        toast((t) => (
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center mb-2">
                <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span className="font-semibold text-red-800">Campos Obligatorios Faltantes</span>
              </div>
              <div className="text-sm text-gray-700" style={{ whiteSpace: 'pre-line' }}>
                {message}
              </div>
            </div>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="ml-3 text-gray-400 hover:text-gray-600 transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ), {
          duration: Infinity,
          position: 'top-center',
          style: {
            maxWidth: '500px'
          }
        })
      }
      return
    }

    const bodegaResuelta = resolveBodegaRecogidaForSubmit(formData, {
      idMarket: bodegaRecogidaSeleccion,
      recogeRespuesta
    })
    const recogeFlag = recogeFlagFromRespuesta(bodegaResuelta.recogeRespuesta)

    if (!bodegaResuelta.idMarket || !BODEGA_FACTURACION_VALUES.has(bodegaResuelta.idMarket)) {
      const labels = bodegaFacturacion
        .map((b: { label: string }) => b.label)
        .join(', ')
      toast.error(`Seleccione la bodega (${labels})`, {
        icon: '⚠️',
        position: 'top-center'
      })
      return
    }

    if (recogeFlag == null) {
      toast.error('Indique si el cliente recoge en bodega', {
        icon: '⚠️',
        position: 'top-center'
      })
      return
    }
    
    if (!onSubmit) return

    const bodegaRecogidaIdApi = bodegaResuelta.idMarket

    const paymentMethod = (formData.extraDetails?.payment_method || formData.payment_method || '').toString().trim().toLowerCase()
    const isPartOrPlanSepare = paymentMethod === 'part' || paymentMethod === 'plan_separe'
    const isPlanSepare = paymentMethod === 'plan_separe'
    const plazoSeleccionado = Number(formData.extraDetails?.plazo)

    let statusParaEnvio = formData.status
    if (mode === 'create' && paymentMethod) {
      statusParaEnvio = getDefaultOrderStatusByPaymentMethod(paymentMethod, userRoleId)
    }

    setIsSubmitting(true)
    
    // Calcular totales
    const totals = calculateTotals(formData.lineItems || [])
    
    // Calcular forma_pago automáticamente si falta pero hay payment_method
    let formaPagoCalculada = formData.extraDetails?.forma_pago
    // Datafono: siempre Contado (6); sobreescribe valores viejos p. ej. crédito (4)
    if (paymentMethod === 'dataf') {
      formaPagoCalculada = '6'
    } else if (!formaPagoCalculada && paymentMethod) {
      if (paymentMethod === 'cod' || paymentMethod === 'cmdm' || paymentMethod === 'addi' || paymentMethod === 'plan_separe') {
        formaPagoCalculada = '4' // Crédito/Contra entrega
      } else if (paymentMethod === 'bold') {
        formaPagoCalculada = '6' // Bold: Contado (coherente con useOrderForm y useOrderPaymentSync)
      } else if (paymentMethod === 'part' || paymentMethod === 'plan_separe') {
        // Para pagos parciales, depende de si hay crédito en los pagos
        const tieneCredito = pagosParciales.some(pago => 
          pago.metodo_pago === 'cod' || pago.metodo_pago === 'cmdm' || pago.metodo_pago === 'addi'
        )
        formaPagoCalculada = tieneCredito ? '4' : '6'
      } else {
        formaPagoCalculada = '6' // Por defecto contado
      }
    }
    
    // Preparar datos del formulario
    const formDataWithProductDetails = {
      ...formData,
      productDetails,
      status: statusParaEnvio,
      total: totals.total,
      date_modified: new Date().toISOString(),
      extraDetails: {
        ...formData.extraDetails,
        forma_pago: formaPagoCalculada || formData.extraDetails?.forma_pago || '6',
        costo_envio: costoEnvio,
        id_market: bodegaRecogidaIdApi,
        recoge_en_bodega: recogeFlag,
        pagos: isPartOrPlanSepare ? pagosParciales.map(pago => {
          const esCod = (pago.metodo_pago || '').toString().trim().toLowerCase() === 'cod'
          const valorNum = Number(pago.valor) || 0
          const valorEnvio =
            esCod && valorNum <= 0 ? '0' : (pago.valor != null ? String(pago.valor) : '0')
          const verificado =
            esCod || Number(pago.pago_verificado) === 1 ? 1 : 0
          return {
            metodo_pago: pago.metodo_pago,
            valor: valorEnvio,
            id_transaccion: pago.id_transaccion || null,
            dcto: pago.dcto ? String(pago.dcto) : null,
            forma_pago: formaPagoCalculada || formData.extraDetails?.forma_pago || '6',
            plazo: isPlanSepare
              ? (Number.isFinite(plazoSeleccionado) && plazoSeleccionado > 0 ? plazoSeleccionado : 30)
              : (Number(pago.plazo) || (Number.isFinite(plazoSeleccionado) && plazoSeleccionado > 0 ? plazoSeleccionado : 30)),
            pago_verificado: verificado,
            fecha_creacion: pago.fecha_creacion || new Date().toISOString()
          }
        }) : undefined
      },
      id_market: bodegaRecogidaIdApi,
      recoge_en_bodega: recogeFlag,
      meta_data: {
        id_market: bodegaRecogidaIdApi,
        recoge_en_bodega: recogeFlag,
        fecha_entrega: fechaEntregaActiva && fechaEntrega ? fechaEntrega : ''
      }
    } as OrderData

    // Agregar datos antiguos en modo edit
    if (mode === 'edit') {
      formDataWithProductDetails.old_data_order = oldDataOrder
    }

    // Transformar y enviar datos
    const transformedData = transformOrderData(formDataWithProductDetails)
    Promise.resolve(onSubmit(transformedData as OrderData)).finally(() => setIsSubmitting(false))
  }, [
    validateForm,
    getValidationSummary,
    formData,
    productDetails,
    costoEnvio,
    pagosParciales,
    recogeRespuesta,
    bodegaRecogidaSeleccion,
    fechaEntregaActiva,
    fechaEntrega,
    mode,
    oldDataOrder,
    onSubmit,
    calculateTotals,
    setIsSubmitting,
    userRoleId
  ])

  return {
    handleSubmit
  }
}

